import { z } from 'zod';
import { bdtAmountSchema, isoDateSchema, labelledUuid, uuidSchema } from './common.js';

/**
 * FDR lifecycle status. Mirrors the Prisma `FdrStatus` enum exactly.
 *  - ACTIVE  : earning interest, principal counted in "battalion strength".
 *  - MATURED : reached its maturity date; principal + earnings flow back.
 *  - CLOSED  : prematurely closed (penalty applied off-system).
 */
export const FdrStatus = {
  ACTIVE: 'ACTIVE',
  MATURED: 'MATURED',
  CLOSED: 'CLOSED',
} as const;
export type FdrStatus = (typeof FdrStatus)[keyof typeof FdrStatus];

export const fdrStatusSchema = z.enum([FdrStatus.ACTIVE, FdrStatus.MATURED, FdrStatus.CLOSED]);

/**
 * Account number raw input: stored masked at rest. Accept 4 to 40 digits/spaces/dashes
 * so most bank formats slot in, then the service masks to the last 4.
 */
const accountNoSchema = z
  .string({ message: 'Account number is required' })
  .min(4, 'Account number must be at least 4 characters')
  .max(40, 'Account number must be at most 40 characters')
  .regex(/^[0-9 \-]+$/, 'Account number must be digits, spaces, or dashes only')
  // Reject inputs whose digit-portion is shorter than 4 — e.g. "----" or
  // "1-2" satisfy the length+character rules but would mask to "****"
  // with fewer than 4 real digits, defeating the masking guarantee.
  .refine((v) => v.replace(/[\s-]/g, '').length >= 4, {
    message: 'Account number must contain at least 4 digits',
  });

const interestRateSchema = z
  .union([z.string(), z.number()], { message: 'Interest rate is required' })
  .transform((v, ctx) => {
    const raw = typeof v === 'number' ? v.toString() : v.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Interest rate must be a percent with up to 2 decimals',
      });
      return z.NEVER;
    }
    const n = Number.parseFloat(raw);
    if (n < 0) {
      ctx.addIssue({ code: 'custom', message: 'Interest rate cannot be negative' });
      return z.NEVER;
    }
    if (n > 100) {
      ctx.addIssue({ code: 'custom', message: 'Interest rate cannot exceed 100%' });
      return z.NEVER;
    }
    return raw;
  });

const fdrBaseShape = {
  bankName: z
    .string({ message: 'Bank name is required' })
    .min(1, 'Bank name is required')
    .max(120, 'Bank name must be at most 120 characters'),
  branch: z.string().max(120, 'Branch must be at most 120 characters').optional().nullable(),
  /** Raw account number — backend masks to last 4 before storage. */
  accountNo: accountNoSchema,
  principalAmount: bdtAmountSchema,
  interestRate: interestRateSchema,
  startDate: isoDateSchema,
  maturityDate: isoDateSchema,
  linkedFundId: labelledUuid('fund to link to this FDR').nullable().optional(),
  notes: z.string().max(1000, 'Notes must be at most 1000 characters').optional().nullable(),
} as const;

export const createFdrRequestSchema = z
  .object(fdrBaseShape)
  .refine((v) => v.maturityDate > v.startDate, {
    message: 'Maturity date must be after start date',
    path: ['maturityDate'],
  });
export type CreateFdrRequest = z.infer<typeof createFdrRequestSchema>;

/**
 * Update mirror — every field optional but `maturityDate > startDate` is
 * re-checked when either side is touched. Status is mutated via the
 * dedicated mark-matured / close actions, not via a generic PATCH.
 */
export const updateFdrRequestSchema = z
  .object({
    bankName: fdrBaseShape.bankName.optional(),
    branch: fdrBaseShape.branch,
    accountNo: accountNoSchema.optional(),
    principalAmount: fdrBaseShape.principalAmount.optional(),
    interestRate: fdrBaseShape.interestRate.optional(),
    startDate: fdrBaseShape.startDate.optional(),
    maturityDate: fdrBaseShape.maturityDate.optional(),
    linkedFundId: fdrBaseShape.linkedFundId,
    notes: fdrBaseShape.notes,
  })
  .refine(
    (v) => {
      if (v.startDate === undefined || v.maturityDate === undefined) return true;
      return v.maturityDate > v.startDate;
    },
    { message: 'Maturity date must be after start date', path: ['maturityDate'] },
  );
export type UpdateFdrRequest = z.infer<typeof updateFdrRequestSchema>;

export const fdrListQuerySchema = z.object({
  status: fdrStatusSchema.optional(),
  linkedFundId: uuidSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type FdrListQuery = z.infer<typeof fdrListQuerySchema>;

export const fdrGrowthQuerySchema = z.object({
  months: z.coerce.number().int().min(1).max(60).default(12),
});
export type FdrGrowthQuery = z.infer<typeof fdrGrowthQuerySchema>;

export const fdrUpcomingQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(90),
});
export type FdrUpcomingQuery = z.infer<typeof fdrUpcomingQuerySchema>;

export interface FdrResponse {
  id: string;
  bankName: string;
  branch: string | null;
  /** Always masked: "****1234". Full number never returned by the API. */
  accountNoMasked: string;
  principalAmount: string;
  /** Percent, e.g. "7.25". */
  interestRate: string;
  startDate: string;
  maturityDate: string;
  status: FdrStatus;
  linkedFundId: string | null;
  linkedFundCode: string | null;
  linkedFundName: string | null;
  /** Whole days from today (Asia/Dhaka) to maturity. Negative once past due. */
  daysToMaturity: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Aggregate card data — drives the "Battalion Financial Strength" panel. */
export interface FdrSummaryResponse {
  /** Sum of `principalAmount` over ACTIVE FDRs only. */
  totalActivePrincipal: string;
  /** Count of ACTIVE FDRs. */
  activeCount: number;
  /** Principal-weighted average interest rate across ACTIVE FDRs. */
  weightedAverageRate: string;
  /**
   * Year-over-year % change in active principal — comparing today's total
   * with the active total exactly 12 months ago. Null when we don't have
   * a comparable history yet (no FDR existed 12 months back).
   */
  yoyPercent: number | null;
}

/** One bar in the growth chart. `month` is YYYY-MM (Asia/Dhaka calendar). */
export interface FdrGrowthPoint {
  month: string;
  totalActivePrincipal: string;
}

export interface FdrUpcomingMaturity {
  id: string;
  bankName: string;
  accountNoMasked: string;
  principalAmount: string;
  maturityDate: string;
  daysToMaturity: number;
}
